class PresenceManager {
    constructor(websocket, simulationId, userId, userName) {
        this.ws = websocket;
        this.simulationId = simulationId;
        this.userId = userId;
        this.userName = userName;
        this.activeUsers = new Map();  // userId -> {name, color, lastActive}

        this.createPresenceBar();

        this.init();
    }

    init() {
        // Setup the Take Control button
        this.setupTakeControlButton();

        // Send presence heartbeat every 10 seconds
        this.heartbeatInterval = setInterval(() => {
            if (this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({
                    type: 'presence_heartbeat'
                }));
            }
        }, 10000);

        // Initial render
        this.render();
    }

    createPresenceBar() {
        // Check if exists
        if (document.getElementById('presence-bar')) return;

        const presenceBar = document.createElement('div');
        presenceBar.id = 'presence-bar';
        presenceBar.className = 'presence-bar';
        presenceBar.innerHTML = `
            <div class="presence-label">Collaborators:</div>
            <div id="presence-users" class="presence-users"></div>
            <button id="btnTakeControl" class="btn-control">🎮 Take Control</button>
        `;

        // Add to main container or header
        const container = document.querySelector('.controls-container') || document.body;
        container.insertBefore(presenceBar, container.firstChild);
    }

    updateList(users) {
        this.activeUsers.clear();
        users.forEach(u => {
            if (u.user_id !== this.userId) {
                this.activeUsers.set(u.user_id, {
                    name: u.user_name,
                    color: u.color || '#ccc'
                });
            }
        });
        this.render();
    }

    // Alias for message handler compatibility
    updateUserList(users) {
        this.updateList(users);
    }

    onUserJoined(userId, userName) {
        if (userId === this.userId) return;

        console.log(`${userName} joined`);
        this.activeUsers.set(userId, {
            name: userName,
            color: this.getUserColor(userId)
        });
        this.render();
    }

    onUserLeft(userId) {
        this.activeUsers.delete(userId);
        this.render();
    }

    render() {
        const container = document.getElementById('presence-users');
        if (!container) return;

        this.container = container;
        container.innerHTML = '';

        // Add Myself
        const myBadge = this.createBadge(this.userName, this.getUserColor(this.userId), true, null);
        container.appendChild(myBadge);

        // Add other users (clickable to follow)
        this.activeUsers.forEach((user, id) => {
            const badge = this.createBadge(user.name, this.getUserColor(id), false, id);
            if (id === this.followingUserId) {
                badge.classList.add('following');
            }
            container.appendChild(badge);
        });
    }

    createBadge(name, color, isMe = false, userId = null) {
        const div = document.createElement('div');
        div.className = 'presence-badge';
        div.style.backgroundColor = color;
        div.title = isMe ? `${name} (You)` : `${name} - Click to follow`;
        div.textContent = name.substring(0, 2).toUpperCase();
        if (isMe) {
            div.style.border = '2px solid white';
        } else if (userId) {
            div.style.cursor = 'pointer';
            div.dataset.userId = userId;
            div.dataset.userName = name;
            div.addEventListener('click', () => this.toggleFollow(userId, name));
        }
        return div;
    }

    toggleFollow(userId, userName) {
        if (this.followingUserId === userId) {
            // Stop following
            this.followingUserId = null;
            this.updateFollowUI();
            Utils?.notify?.('Stopped following ' + userName, 'info');
        } else {
            // Start following
            this.followingUserId = userId;
            this.updateFollowUI();
            Utils?.notify?.('Now following ' + userName + "'s view", 'success');

            // Notify the followed user (optional)
            if (this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({
                    type: 'follow_request',
                    data: { target_user_id: userId }
                }));
            }
        }

        // Tell viewer about follow state
        if (window.viewer) {
            window.viewer.followingUserId = this.followingUserId;
        }
    }

    updateFollowUI() {
        // Update badge styles to show who we're following
        const badges = this.container?.querySelectorAll('.presence-badge[data-user-id]') || [];
        badges.forEach(badge => {
            if (badge.dataset.userId === this.followingUserId) {
                badge.classList.add('following');
            } else {
                badge.classList.remove('following');
            }
        });
    }

    getUserColor(userId) {
        const colors = [
            '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
            '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50'
        ];
        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
            hash = userId.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }

    setupTakeControlButton() {
        const btn = document.getElementById('btnTakeControl');
        if (!btn) return;

        btn.addEventListener('click', () => {
            if (window.lockManager) {
                if (window.lockManager.hasLock('camera')) {
                    // Release control
                    window.lockManager.releaseLock('camera');
                } else {
                    // Request control
                    window.lockManager.requestLock('camera', 'Camera Control');
                }
            }
        });
    }

    // Inject CSS styles
    static injectStyles() {
        if (document.getElementById('presence-bar-styles')) return;

        const style = document.createElement('style');
        style.id = 'presence-bar-styles';
        style.textContent = `
            .presence-bar {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 8px 16px;
                background: rgba(0, 0, 0, 0.8);
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 1000;
                backdrop-filter: blur(10px);
            }

            .presence-label {
                color: rgba(255, 255, 255, 0.7);
                font-size: 12px;
                font-weight: 500;
            }

            .presence-users {
                display: flex;
                gap: 6px;
                flex: 1;
            }

            .presence-badge {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: 600;
                color: white;
                cursor: default;
                transition: transform 0.2s;
            }

            .presence-badge:hover {
                transform: scale(1.1);
            }

            .btn-control {
                padding: 6px 12px;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                border-radius: 6px;
                color: white;
                font-size: 12px;
                cursor: pointer;
                transition: all 0.2s;
            }

            .btn-control:hover:not(:disabled) {
                background: rgba(255, 255, 255, 0.2);
            }

            .btn-control:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .btn-control.active {
                background: #4CAF50;
                border-color: #4CAF50;
            }

            .presence-badge.following {
                box-shadow: 0 0 0 3px #2196F3, 0 0 10px rgba(33, 150, 243, 0.5);
                animation: pulse-follow 2s infinite;
            }

            @keyframes pulse-follow {
                0%, 100% { box-shadow: 0 0 0 3px #2196F3, 0 0 10px rgba(33, 150, 243, 0.5); }
                50% { box-shadow: 0 0 0 3px #2196F3, 0 0 20px rgba(33, 150, 243, 0.8); }
            }
        `;
        document.head.appendChild(style);
    }
}

// Inject styles on load
PresenceManager.injectStyles();
